import React, { useEffect, useState } from "react";
import { api, formatGHS, formatApiErrorDetail } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];

const statusClass = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-600",
};

const paymentClass = {
  paid: "bg-green-100 text-green-800",
  paid_offline: "bg-green-100 text-green-800",
  pending_contact: "bg-yellow-100 text-yellow-800",
  initiated: "bg-yellow-100 text-yellow-800",
  unpaid: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
};

const paymentLabel = {
  paid: "Paid",
  paid_offline: "Paid (offline)",
  pending_contact: "Awaiting contact",
  initiated: "Initiated",
  unpaid: "Unpaid",
  cancelled: "Cancelled",
};

export default function AdminBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const q = filter !== "all" ? `?status_filter=${filter}` : "";
    api.get(`/admin/bookings${q}`).then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updatePayment = async (id, payment_status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { payment_status });
      toast.success(payment_status === "paid_offline" ? "Marked as paid · customer promoted in funnel" : "Payment status updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8" data-testid="admin-bookings-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="eyebrow">Bookings</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">All bookings</h1>
        </div>
        <div className="flex items-center gap-2">
          <select className="titan-input" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-bookings-filter">
            <option value="all">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#e63946]" /></div>
      ) : bookings.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">No bookings yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-bookings-table">
            <thead className="bg-[#0d2040] text-white">
              <tr>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Ref</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Customer</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Car</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Dates</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Days</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Total</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Payment</th>
                <th className="text-left p-3 font-bold uppercase tracking-wider text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-gray-100 hover:bg-[#f7f8fa]">
                  <td className="p-3 font-mono text-xs">{b.reference}</td>
                  <td className="p-3">
                    <div className="font-medium text-[#0d2040]">{b.customer_name}</div>
                    <div className="text-xs text-gray-500">{b.customer_email}</div>
                    <div className="text-xs text-gray-500">{b.customer_phone}</div>
                  </td>
                  <td className="p-3">{b.car_name}</td>
                  <td className="p-3 text-xs">
                    {b.pickup_date} → {b.return_date}<br />
                    <span className="text-gray-500">{b.pickup_location}</span>
                  </td>
                  <td className="p-3">{b.days}</td>
                  <td className="p-3 font-display font-bold text-[#e63946]">{formatGHS(b.total_amount)}</td>
                  <td className="p-3">
                    <select
                      value={b.payment_status || "pending_contact"}
                      onChange={(e) => updatePayment(b.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border-0 ${paymentClass[b.payment_status] || "bg-gray-100"}`}
                      data-testid={`admin-payment-select-${b.id}`}
                    >
                      <option value="pending_contact">Awaiting payment</option>
                      <option value="paid_offline">Paid</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </td>
                  <td className="p-3">
                    <select
                      value={b.status}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border-0 ${statusClass[b.status] || "bg-gray-100"}`}
                      data-testid={`admin-status-select-${b.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
